import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Waveform } from '@/components/Waveform';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Mic, MicOff, Sparkles, Pin, Trash2, FileText, ListChecks, Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const COLORS = { cream: '#F4EFDF', mint: '#BEEBD6', gold: '#E9D79A', sage: '#A6C3B4' };

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', color: 'mint', project_id: '' });
  const [recording, setRecording] = useState(false);
  const [analyser, setAnalyser] = useState(null);
  const [detail, setDetail] = useState(null);
  const [processing, setProcessing] = useState(false);
  const recRef = useRef(null);
  const streamRef = useRef(null);

  const load = async () => {
    const [n, p] = await Promise.all([api.get('/notes'), api.get('/projects').catch(() => ({ data: [] }))]);
    setNotes(n.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const startRec = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser(); an.fftSize = 128; src.connect(an); setAnalyser(an);
    } catch (e) { toast.error('Micrófono no disponible'); return; }
    if (SR) {
      const rec = new SR(); rec.lang = 'es-ES'; rec.continuous = true; rec.interimResults = true;
      rec.onresult = (ev) => {
        let txt = '';
        for (let i = 0; i < ev.results.length; i++) txt += ev.results[i][0].transcript;
        setForm((f) => ({ ...f, content: txt }));
      };
      rec.onerror = () => {};
      rec.start(); recRef.current = rec;
    } else { toast.info('Transcripción por voz no soportada en este navegador; puedes escribir la nota.'); }
    setRecording(true);
  };
  const stopRec = () => {
    setRecording(false);
    if (recRef.current) recRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    setAnalyser(null);
  };

  const save = async () => {
    if (!form.title && !form.content) return toast.error('Escribe algo');
    if (recording) stopRec();
    await api.post('/notes', { ...form, kind: recording ? 'voice' : 'text', project_id: form.project_id || null, title: form.title || form.content.slice(0, 40) });
    toast.success('Nota guardada'); setOpen(false); setForm({ title: '', content: '', color: 'mint', project_id: '' }); load();
  };

  const process = async (note) => {
    setProcessing(true);
    try {
      const { data } = await api.post(`/ai/process-note/${note.id}`);
      setDetail({ ...note, summary: data.summary, extracted_tasks: data.extracted_tasks });
      load();
      toast.success('Nota procesada con IA');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al procesar'); }
    setProcessing(false);
  };

  const createTasks = async (note) => {
    const tasks = note.extracted_tasks || [];
    if (!tasks.length) return toast.info('No hay tareas para crear');
    await api.post('/ai/extract-tasks', { text: note.content, project_id: note.project_id, create: true });
    toast.success(`${tasks.length} tareas creadas`);
  };

  const del = async (id) => { setNotes(notes.filter((n) => n.id !== id)); await api.delete(`/notes/${id}`).catch(() => load()); };
  const togglePin = async (n) => { await api.patch(`/notes/${n.id}`, { pinned: !n.pinned }); load(); };

  const exportPdf = (note) => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text(note.title || 'Nota', 14, 20);
    doc.setFontSize(10); doc.text(doc.splitTextToSize(note.content || '', 180), 14, 32);
    if (note.summary?.summary) { doc.setFontSize(12); doc.text('Resumen:', 14, 70); doc.setFontSize(10); doc.text(doc.splitTextToSize(note.summary.summary, 180), 14, 78); }
    doc.save(`${(note.title || 'nota').slice(0, 20)}.pdf`);
  };

  return (
    <div data-testid="notes-module">
      <PageHeader kicker="Notas Inteligentes" title="Notas con IA" subtitle="Escribe o dicta. La IA transcribe, resume y extrae tareas automáticamente."
        actions={<button data-testid="new-note-btn" onClick={() => setOpen(true)} className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}><Plus size={16} /> Nota</button>} />

      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
        {notes.map((n) => (
          <motion.div key={n.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid={`note-${n.id}`}
            className={`pp-glass p-5 break-inside-avoid group ${n.pinned ? 'pp-glow-gold' : ''}`} style={{ borderLeft: `4px solid ${COLORS[n.color] || COLORS.mint}` }}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-semibold">{n.title}</h3>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                <button onClick={() => togglePin(n)} className={n.pinned ? 'pp-gold' : 'text-muted-foreground'}><Pin size={14} /></button>
                <button onClick={() => del(n.id)} className="text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">{n.content}</p>
            {n.summary?.summary && <div className="mt-3 p-2 rounded-lg bg-[rgba(62,180,137,0.08)] text-xs"><span className="pp-mint font-medium">Resumen IA:</span> {n.summary.summary}</div>}
            {(n.extracted_tasks?.length > 0) && <div className="mt-1 text-[11px] pp-mint flex items-center gap-1"><ListChecks size={12} /> {n.extracted_tasks.length} tareas detectadas</div>}
            <div className="flex gap-2 mt-3">
              <button data-testid={`process-note-${n.id}`} onClick={() => process(n)} className="text-xs px-2 h-8 rounded-lg border flex items-center gap-1 pp-mint"><Sparkles size={12} /> Procesar IA</button>
              <button onClick={() => setDetail(n)} className="text-xs px-2 h-8 rounded-lg border flex items-center gap-1"><FileText size={12} /> Ver</button>
            </div>
          </motion.div>
        ))}
        {notes.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground">Sin notas aún.</div>}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!o && recording) stopRec(); setOpen(o); }}>
        <DialogContent className="pp-glass-strong"><DialogHeader><DialogTitle>Nueva nota</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="note-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            {recording && <div className="p-3 rounded-xl bg-[rgba(62,180,137,0.06)]"><Waveform analyser={analyser} active={recording} /></div>}
            <textarea data-testid="note-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Escribe o dicta tu nota..." className="w-full h-32 p-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <div className="flex gap-2">
              {Object.entries(COLORS).map(([k, v]) => <button key={k} onClick={() => setForm({ ...form, color: k })} className={`w-7 h-7 rounded-full border-2 ${form.color === k ? 'border-white' : 'border-transparent'}`} style={{ background: v }} />)}
              <div className="flex-1" />
              <button data-testid="note-record-btn" onClick={recording ? stopRec : startRec} className={`h-9 px-3 rounded-lg text-xs flex items-center gap-1 ${recording ? 'text-white' : 'border pp-mint'}`} style={recording ? { background: '#E35D6A' } : {}}>{recording ? <><MicOff size={14} /> Detener</> : <><Mic size={14} /> Dictar</>}</button>
            </div>
            <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none"><option value="">Vincular a proyecto (opcional)</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            <button data-testid="note-save" onClick={save} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="pp-glass-strong max-w-lg"><DialogHeader><DialogTitle>{detail?.title}</DialogTitle></DialogHeader>
          {detail && <div className="space-y-4 max-h-[60vh] overflow-auto pp-scroll">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{detail.content}</p>
            {processing && <div className="flex items-center gap-2 text-sm pp-mint"><Loader2 size={16} className="animate-spin" /> Procesando con IA...</div>}
            {detail.summary?.summary && <div className="p-3 rounded-xl bg-[rgba(62,180,137,0.08)]"><div className="text-xs pp-mint font-semibold mb-1">Resumen ejecutivo</div><p className="text-sm">{detail.summary.summary}</p>{detail.summary.next_actions?.length > 0 && <ul className="mt-2 text-xs text-muted-foreground list-disc pl-4">{detail.summary.next_actions.map((a, i) => <li key={i}>{a}</li>)}</ul>}</div>}
            {detail.extracted_tasks?.length > 0 && <div className="p-3 rounded-xl bg-[rgba(201,168,76,0.08)]"><div className="text-xs pp-gold font-semibold mb-2">Tareas detectadas</div>{detail.extracted_tasks.map((t, i) => <div key={i} className="text-sm flex items-center gap-2 mb-1"><ListChecks size={13} className="pp-gold" /> {t.title} {t.owner && <span className="text-[11px] text-muted-foreground">· {t.owner}</span>}</div>)}</div>}
            <div className="flex gap-2">
              <button onClick={() => process(detail)} className="flex-1 h-10 rounded-xl text-sm border flex items-center justify-center gap-1 pp-mint"><Wand2 size={14} /> Procesar IA</button>
              {detail.extracted_tasks?.length > 0 && <button onClick={() => createTasks(detail)} className="flex-1 h-10 rounded-xl text-sm font-medium text-[#07090A] flex items-center justify-center gap-1" style={{ background: '#3EB489' }}><ListChecks size={14} /> Crear tareas</button>}
              <button onClick={() => exportPdf(detail)} className="h-10 px-3 rounded-xl text-sm border"><FileText size={14} /></button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
